// IntentAnalyzerSpace.js - Version mise à jour pour intégration sourceService

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { 
  Target, 
  Home, 
  Navigation, 
  Globe, 
  FileEdit, 
  ChevronRight,
  Zap,
  Lightbulb,
  AlertCircle,
  MessageCircle,
  Search,
  Sparkles,
  Shield
} from 'lucide-react';

// Import des modules
import ConversationManager from './ConversationManager';
import AnalysisManager from './AnalysisManager';
import ObjectifEditor from './ObjectifEditor';
import InspirationBreadcrumb from './InspirationBreadCrumb';

// Styles harmonisés (inchangés)
const harmonizedStyles = `
.page-title { 
  font-size: 1.75rem; 
  line-height: 2.25rem; 
  font-weight: 700; 
}
.section-title { 
  font-size: 1.5rem; 
  line-height: 2rem; 
  font-weight: 600; 
}
.main-text { 
  font-size: 1.125rem; 
  line-height: 1.75rem; 
}
.tab-container { 
  background: white; 
  border-radius: 0.75rem; 
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); 
  border: 1px solid #e5e7eb; 
  overflow: hidden; 
}
.tab-header { 
  padding: 1rem; 
  border-bottom: 1px solid #f3f4f6; 
  background: linear-gradient(135deg, #f8fafc, #e2e8f0); 
}
.tab-header-gradient-blue { 
  background: linear-gradient(135deg, #dbeafe, #bfdbfe); 
}
.tab-header-gradient-purple { 
  background: linear-gradient(135deg, #e9d5ff, #d8b4fe); 
}
.tab-header-gradient-green { 
  background: linear-gradient(135deg, #dcfce7, #bbf7d0); 
}
.tab-content { 
  padding: 1.5rem; 
}
.process-step { 
  position: relative; 
  display: flex; 
  align-items: center; 
  padding: 0.5rem 1rem; 
  border-radius: 0.5rem; 
  transition: all 0.3s ease; 
  font-weight: 500; 
}
.process-step.completed { 
  background: linear-gradient(135deg, #dbeafe, #bfdbfe); 
  color: #1e40af; 
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2); 
}
.process-step.active { 
  background: linear-gradient(135deg, #dcfce7, #bbf7d0); 
  color: #166534; 
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3); 
  font-weight: 600; 
}
.process-step.pending { 
  background: linear-gradient(135deg, #dbeafe, #bfdbfe); 
  color: #1e40af; 
}
.process-step:hover { 
  transform: translateY(-1px); 
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); 
}
`;

/**
 * Composant principal orchestrateur - IntentAnalyzerSpace
 */
const IntentAnalyzerSpace = () => {
  const { competenceId } = useParams();
  const navigate = useNavigate();

  // États globaux principaux
  const [userObjective, setUserObjective] = useState('');
  const [competenceData, setCompetenceData] = useState(null);
  const [activeTab, setActiveTab] = useState('objectif');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);

  // ✨ 1: États de conversation mis à jour pour sourceService
  const [conversationData, setConversationData] = useState({
    // Conversation libre
    freeMessages: [],
    conversationPhase: 'generating', // 'generating' | 'guided'
    
    // Questions guidées
    aiQuestions: [],
    guidedAnswers: {},
    objectiveUpdatedFromAnswers: false,
    lastObjectiveUpdate: null,
    
    // Réponse IA avec sources via sourceService
    aiResponse: '',
    aiResponseGenerated: false,
    originalObjectiveForAI: '',
    objectiveHasChangedSinceAI: false,
    
    // ✨ Sources via sourceService avec métadonnées enrichies
    relevantLinks: [], // Sources avec informations d'accessibilité complètes
    sourcesProvider: 'DuckDuckGo', // Provider utilisé
    sourcesAccessibilityMode: 'enhanced', // Mode de vérification d'accessibilité
    sourcesGenerationTime: null, // Timestamp de génération
    sourcesStats: null, // Statistiques de génération (optionnel)
    
    // Autres données
    analysisCompleted: false
  });

  const [analysisData, setAnalysisData] = useState({
    results: null,
    completed: false,
    optimizedQuery: '',
    readyToSearch: false,
    triggerAnalysis: false
  });

  // ✨ 2: Récupération des données avec support sourceService
  useEffect(() => {
    const fetchCompetence = async () => {
      if (!competenceId) return;
      setIsLoading(true);

      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          navigate('/');
          return;
        }
        
        const docRef = doc(db, 'users', currentUser.uid, 'competences', competenceId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = { ...docSnap.data(), id: competenceId };
          setCompetenceData(data);
          
          if (data.title) {
            setUserObjective(data.title);
            
            // ✨ Charger données de conversation avec sources sourceService
            if (data.conversationData) {
              const savedConversationData = data.conversationData;
              setConversationData(prev => ({
                ...prev,
                ...savedConversationData,
                // Normaliser la phase de conversation (suppression du mode libre)
                conversationPhase: savedConversationData.conversationPhase === 'free_chat'
                  ? 'generating'
                  : (savedConversationData.conversationPhase || 'generating'),
                // S'assurer que les nouvelles propriétés existent
                relevantLinks: savedConversationData.relevantLinks || [],
                sourcesProvider: savedConversationData.sourcesProvider || 'openai',
                sourcesAccessibilityMode: savedConversationData.sourcesAccessibilityMode || 'enhanced'
              }));
            }
            
            // Charger analyse existante si disponible
            if (data.semanticAnalysis?.results) {
              const analysisDataFromDB = data.semanticAnalysis;
              if (analysisDataFromDB.objective === data.title && analysisDataFromDB.status === 'completed') {
                setAnalysisData({
                  results: analysisDataFromDB.results,
                  completed: true,
                  optimizedQuery: analysisDataFromDB.optimizedQuery || '',
                  readyToSearch: !!analysisDataFromDB.optimizedQuery,
                  triggerAnalysis: false
                });
              }
            }
          }
        } else {
          setError("Cette compétence n'existe pas ou vous n'avez pas les droits pour y accéder.");
        }
      } catch (err) {
        console.error("Erreur lors de la récupération de la compétence :", err);
        setError("Une erreur s'est produite lors du chargement.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompetence();
  }, [competenceId, navigate]);

  // ✨ 3: Gestion mise à jour objectif inchangée
  const handleObjectiveUpdate = useCallback(async (updatedObjectif) => {
    if (!updatedObjectif.trim()) return;

    const previousObjective = userObjective;
    setUserObjective(updatedObjectif);
    setLastUpdateTime(new Date().toLocaleTimeString());

    // Vérifier si objectif a changé depuis réponse IA
    if (conversationData.aiResponseGenerated && updatedObjectif !== conversationData.originalObjectiveForAI) {
      setConversationData(prev => ({
        ...prev,
        objectiveHasChangedSinceAI: true
      }));
    }

    // Sauvegarder en Firestore
    if (competenceId && auth.currentUser) {
      try {
        await updateDoc(doc(db, 'users', auth.currentUser.uid, 'competences', competenceId), {
          title: updatedObjectif,
          updatedAt: serverTimestamp()
        });
      } catch (error) {
        console.error('Erreur de mise à jour:', error);
      }
    }
  }, [competenceId, conversationData.aiResponseGenerated, conversationData.originalObjectiveForAI, userObjective]);

  // ✨ 4: Gestion des données de conversation avec sauvegarde
  const handleConversationUpdate = useCallback(async (data) => {
    console.log('🔄 Mise à jour conversation data:', data);
    
    setConversationData(prev => {
      const updatedData = { ...prev, ...data };
      
      // ✨ Ajouter timestamp pour les sources si nouvelles sources générées
      if (data.relevantLinks && data.relevantLinks.length > 0) {
        updatedData.sourcesGenerationTime = new Date().toISOString();
        
        // Extraire les stats des sources si disponibles
        if (data.relevantLinks[0]?.backendType === 'sourceService') {
          updatedData.sourcesStats = {
            provider: data.relevantLinks[0]?.provider || 'openai',
            accessibilityMode: 'enhanced',
            sourcesCount: data.relevantLinks.length,
            accessibleSources: data.relevantLinks.filter(link => link.accessible).length,
            fallbackSources: data.relevantLinks.filter(link => link.hasUrlFallback).length
          };
        }
      }
      
      return updatedData;
    });

    // ✨ Sauvegarde automatique des données de conversation enrichies
    if (competenceId && auth.currentUser) {
      try {
        const updatedConversationData = { ...conversationData, ...data };
        
        // Ajouter metadata sourceService si sources présentes
        if (data.relevantLinks && data.relevantLinks.length > 0) {
          updatedConversationData.sourcesGenerationTime = new Date().toISOString();
          updatedConversationData.sourcesProvider = 'openai';
          updatedConversationData.sourcesAccessibilityMode = 'enhanced';
        }
        
        await updateDoc(doc(db, 'users', auth.currentUser.uid, 'competences', competenceId), {
          conversationData: updatedConversationData,
          updatedAt: serverTimestamp()
        });
        
        console.log('✅ Données de conversation sauvegardées avec sources sourceService');
      } catch (error) {
        console.error('❌ Erreur sauvegarde conversation:', error);
      }
    }
  }, [competenceId, conversationData]);

  // Gestion des données d'analyse 
  const handleAnalysisUpdate = useCallback((data) => {
    setAnalysisData(prev => ({
      ...prev,
      ...data
    }));
  }, []);

  // Fonction pour lancer l'analyse depuis la conversation 
  const handleLaunchAnalysisFromConversation = useCallback(() => {
    setActiveTab('analysis');
    setAnalysisData(prev => ({
      ...prev,
      triggerAnalysis: true
    }));
  }, []);

  // Sauvegarde générale des données 
  const saveAppData = useCallback(async (dataToSave) => {
    if (!competenceId || !auth.currentUser) return { success: false };

    try {
      const currentUser = auth.currentUser;
      const timestamp = serverTimestamp();

      const competenceDocRef = doc(db, 'users', currentUser.uid, 'competences', competenceId);
      await updateDoc(competenceDocRef, {
        ...dataToSave,
        updatedAt: timestamp
      });

      return { success: true };
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      return { success: false, error: error.message };
    }
  }, [competenceId]);

  // ✨ 5: Lancement de la recherche 360° avec données sourceService
  const handleLaunchSearch = useCallback(() => {
    navigate(`/webSource/${competenceId}`, {
      state: {
        semanticAnalysis: analysisData.results,
        optimizedQuery: analysisData.optimizedQuery,
        competence: competenceData,
        aiResponse: conversationData.aiResponse,
        // ✨ Transmettre les données sources sourceService
        sourcesData: {
          relevantLinks: conversationData.relevantLinks,
          sourcesProvider: conversationData.sourcesProvider,
          sourcesStats: conversationData.sourcesStats,
          sourcesGenerationTime: conversationData.sourcesGenerationTime
        }
      }
    });
  }, [navigate, competenceId, analysisData, competenceData, conversationData]);

  // ✨ 6: Notifications pour les onglets avec indicateurs sourceService
  const renderTabNotification = (tabName) => {
    if (tabName === 'ai_response' && conversationData.aiResponseGenerated && activeTab !== 'ai_response') {
      return React.createElement('span', {
        className: "w-2 h-2 bg-purple-500 rounded-full absolute top-0 right-0"
      });
    }
    if (tabName === 'analysis' && analysisData.completed && activeTab !== 'analysis') {
      return React.createElement('span', {
        className: "w-2 h-2 bg-blue-500 rounded-full absolute top-0 right-0"
      });
    }
    return null;
  };

  return React.createElement('div', {
    className: "min-h-screen bg-gradient-to-b from-blue-50 via-indigo-50 to-white"
  }, [

    React.createElement('style', { key: 'styles' }, harmonizedStyles),

    React.createElement('div', {
      key: 'main-content',
      className: "max-w-7xl mx-auto px-4 py-6"
    }, [
      
      React.createElement(InspirationBreadcrumb, {
        key: 'breadcrumb',
        competenceId: competenceId,
        currentPage: 'orientation'
      }),

      // Message d'erreur
      error && React.createElement('div', {
        key: 'error',
        className: "mb-4 bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg"
      }, error),

      // ✨ 8: Système d'onglets avec indicateurs sourceService
      React.createElement('div', {
        key: 'tabs',
        className: "mb-6 border-b border-gray-200"
      },
        React.createElement('div', {
          className: "flex space-x-8"
        }, [
          // Onglet Objectif avec conversation intégrée
          React.createElement('button', {
            key: 'tab-objectif',
            onClick: () => setActiveTab('objectif'),
            className: `py-3 px-1 relative ${
              activeTab === 'objectif'
                ? 'border-b-2 border-green-500 text-green-600 font-medium'
                : 'text-gray-500 hover:text-gray-700'
            }`
          },
            React.createElement('div', {
              className: "flex items-center"
            }, [
              React.createElement(Target, {
                key: 'icon',
                className: "h-5 w-5 mr-2"
              }),
              React.createElement('span', {
                key: 'label'
              }, 'Objectif & Précision'),
              conversationData.objectiveHasChangedSinceAI && React.createElement('span', {
                key: 'alert',
                className: "ml-2"
              },
                React.createElement(AlertCircle, {
                  className: "h-3 w-3 text-orange-500"
                })
              )
            ])
          ),
          
          React.createElement('button', {
            key: 'tab-ai-response',
            onClick: () => setActiveTab('ai_response'),
            className: `py-3 px-1 relative ${
              activeTab === 'ai_response'
                ? 'border-b-2 border-purple-500 text-purple-600 font-medium'
                : 'text-gray-500 hover:text-gray-700'
            }`
          },
            React.createElement('div', {
              className: "flex items-center"
            }, [
              React.createElement(Zap, {
                key: 'icon',
                className: "h-5 w-5 mr-2"
              }),
              React.createElement('span', {
                key: 'label'
              }, 'Propositions'),
              renderTabNotification('ai_response'),
              conversationData.objectiveHasChangedSinceAI && React.createElement('span', {
                key: 'alert',
                className: "ml-2"
              },
                React.createElement(AlertCircle, {
                  className: "h-3 w-3 text-orange-500"
                })
              )
            ])
          ),
          
          React.createElement('button', {
            key: 'tab-analysis',
            onClick: () => setActiveTab('analysis'),
            className: `py-3 px-1 relative ${
              activeTab === 'analysis'
                ? 'border-b-2 border-blue-500 text-blue-600 font-medium'
                : 'text-gray-500 hover:text-gray-700'
            }`
          },
            React.createElement('div', {
              className: "flex items-center"
            }, [
              React.createElement(Lightbulb, {
                key: 'icon',
                className: "h-5 w-5 mr-2"
              }),
              React.createElement('span', {
                key: 'label'
              }, 'Analyse Sémantique'),
              renderTabNotification('analysis')
            ])
          )
        ])
      ),
      
      // ✨ 9: Contenu des onglets avec passage du competenceId
      activeTab === 'objectif' && React.createElement('div', {
        key: 'objectif-content',
        className: "space-y-6"
      }, [
        React.createElement(ObjectifEditor, {
          key: 'objectif-editor',
          objectif: userObjective,
          objectifType: "project",
          onSave: handleObjectiveUpdate,
          isReadOnly: false,
          lastUpdate: lastUpdateTime,
          conversationData: conversationData,
          onConversationUpdate: handleConversationUpdate,
          competenceId: competenceId // ✨ AJOUT: competenceId requis pour sourceService
        }),
        
        // Conversation intégrée pour préciser l'objectif
        userObjective && React.createElement('div', {
          key: 'conversation-section',
          className: "bg-white p-6 rounded-lg border border-gray-200 shadow-sm"
        }, [

          
          React.createElement(ConversationManager, {
            key: 'conversation-manager',
            userObjective: userObjective,
            competenceId: competenceId, // ✨ ESSENTIEL: competenceId pour sourceService
            conversationData: conversationData,
            analysisData: analysisData,
            onConversationUpdate: handleConversationUpdate,
            onAnalysisUpdate: handleAnalysisUpdate,
            onSaveData: saveAppData,
            onSwitchTab: setActiveTab,
            onLaunchAnalysis: handleLaunchAnalysisFromConversation,
            onObjectiveUpdate: handleObjectiveUpdate,
            lastUpdateTime: lastUpdateTime,
            mode: "conversation" // ✨ MODE: conversation pour l'onglet objectif
          })
        ]),
        
        // Actions disponibles après définition de l'objectif
        userObjective && React.createElement('div', {
          key: 'action-cards',
          className: "grid md:grid-cols-2 gap-4"
        }, [
          // Option Proposition avec badge sourceService
          React.createElement('div', {
            key: 'proposition-card',
            className: "bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-100"
          }, [
            React.createElement('div', {
              key: 'header',
              className: "flex items-center mb-3"
            }, [
              React.createElement(Zap, {
                key: 'icon',
                className: "h-5 w-5 text-purple-500 mr-2"
              }),
              React.createElement('h3', {
                key: 'title',
                className: "font-semibold text-purple-800"
              }, 'Obtenir une Proposition'),
              conversationData.aiResponseGenerated && React.createElement('span', {
                key: 'badge',
                className: "ml-2 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full"
              }, '✓ Disponible'),
              // ✨ Badge sourceService
              conversationData.relevantLinks?.length > 0 && React.createElement('span', {
                key: 'sources-badge',
                className: "ml-2 text-xs bg-purple-100 text-purple-600 px-2 py-1 rounded-full"
              }, `🔗 ${conversationData.relevantLinks.length}`)
            ]),
            React.createElement('p', {
              key: 'description',
              className: "text-sm text-purple-700 mb-3"
            }, 'Conseils pratiques instantanés avec sources OpenAI vérifiées automatiquement.'),
            React.createElement('button', {
              key: 'button',
              onClick: () => setActiveTab('ai_response'),
              className: "w-full px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-medium flex items-center justify-center hover:from-purple-600 hover:to-pink-600"
            }, [
              React.createElement(Zap, {
                key: 'icon',
                className: "h-4 w-4 mr-2"
              }),
              conversationData.aiResponseGenerated ? 'Consulter la proposition' : 'Générer une proposition'
            ])
          ]),

          // Option Analyse Complète
          React.createElement('div', {
            key: 'analysis-card',
            className: "bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-100"
          }, [
            React.createElement('div', {
              key: 'header',
              className: "flex items-center mb-3"
            }, [
              React.createElement(Search, {
                key: 'icon',
                className: "h-5 w-5 text-blue-500 mr-2"
              }),
              React.createElement('h3', {
                key: 'title',
                className: "font-semibold text-blue-800"
              }, 'Analyse Sémantique'),
              analysisData.completed && React.createElement('span', {
                key: 'badge',
                className: "ml-2 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full"
              }, '✓ Terminée')
            ]),
            React.createElement('p', {
              key: 'description',
              className: "text-sm text-blue-700 mb-3"
            }, 'Analyse approfondie pour optimiser votre recherche et obtenir des sources spécialisées.'),
            React.createElement('button', {
              key: 'button',
              onClick: () => setActiveTab('analysis'),
              className: "w-full px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg font-medium flex items-center justify-center hover:from-blue-600 hover:to-indigo-600"
            }, [
              React.createElement(Search, {
                key: 'icon',
                className: "h-4 w-4 mr-2"
              }),
              analysisData.completed ? 'Consulter l\'analyse' : 'Lancer l\'analyse'
            ])
          ])
        ]),

        // ✨ 10: Conseils contextuels mis à jour pour sourceService
        React.createElement('div', {
          key: 'tips',
          className: "bg-blue-50 p-4 rounded-lg border border-blue-100"
        },
          React.createElement('div', {
            className: "flex items-start space-x-3"
          }, [
            React.createElement(Lightbulb, {
              key: 'icon',
              className: "h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5"
            }),
            React.createElement('div', {
              key: 'content'
            }, [
              React.createElement('p', {
                key: 'title',
                className: "text-sm font-medium text-blue-800 flex items-center"
              }, [
                '💡 Conseil',
                // ✨ Badge intégration sourceService
                conversationData.relevantLinks?.length > 0 && React.createElement('span', {
                  key: 'integration-badge',
                  className: "ml-2 text-xs bg-gradient-to-r from-green-100 to-blue-100 text-green-700 px-2 py-1 rounded-full flex items-center"
                }, [
                  React.createElement(Sparkles, {
                    key: 'icon',
                    className: "h-3 w-3 mr-1"
                  }),
                  'Intégration sourceService active'
                ])
              ]),
              React.createElement('p', {
                key: 'text',
                className: "text-xs text-blue-700 mt-1"
              }, 
                !userObjective 
                  ? "Commencez par définir clairement votre objectif, puis utilisez la conversation pour le préciser."
                  : Object.keys(conversationData.guidedAnswers || {}).length === 0
                  ? "Objectif défini ! Répondez aux 3 questions personnalisées pour le préciser avec Aladinn."
                  : !conversationData.aiResponseGenerated && !analysisData.completed
                  ? "Conversation engagée ! Vous pouvez maintenant obtenir une proposition rapide avec sources OpenAI vérifiées."
                  : conversationData.objectiveHasChangedSinceAI
                  ? "Votre objectif a été modifié. Regénérez votre proposition pour des conseils adaptés avec nouvelles sources."
                  : conversationData.relevantLinks?.length > 0
                  ? `Parfait ! ${conversationData.relevantLinks.length} sources OpenAI vérifiées disponibles. Explorez les autres onglets.`
                  : "Parfait ! Votre objectif est bien défini. Consultez les autres onglets pour approfondir."
              ),
              // ✨ Affichage des stats sourceService
              conversationData.sourcesStats && React.createElement('div', {
                key: 'stats',
                className: "mt-2 text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded"
              }, [
                React.createElement('span', {
                  key: 'provider'
                }, `Provider: ${conversationData.sourcesStats.provider} • `),
                React.createElement('span', {
                  key: 'accessibility'
                }, `${conversationData.sourcesStats.accessibleSources}/${conversationData.sourcesStats.sourcesCount} accessibles`),
                conversationData.sourcesStats.fallbackSources > 0 && React.createElement('span', {
                  key: 'fallback'
                }, ` • ${conversationData.sourcesStats.fallbackSources} récupérées`)
              ])
            ])
          ])
        )
      ]),
      
      // ✨ 11: Onglet AI Response avec mode ai_response
      activeTab === 'ai_response' && React.createElement(ConversationManager, {
        key: 'ai-response-content',
        userObjective: userObjective,
        competenceId: competenceId, // ✨ ESSENTIEL: competenceId pour sourceService
        conversationData: conversationData,
        analysisData: analysisData,
        onConversationUpdate: handleConversationUpdate,
        onAnalysisUpdate: handleAnalysisUpdate,
        onSaveData: saveAppData,
        onSwitchTab: setActiveTab,
        mode: "ai_response", // ✨ MODE: ai_response pour l'onglet propositions
        onObjectiveUpdate: handleObjectiveUpdate,
        lastUpdateTime: lastUpdateTime
      }),
      
      activeTab === 'analysis' && React.createElement(AnalysisManager, {
        key: 'analysis-content',
        userObjective: userObjective,
        competenceId: competenceId,
        conversationData: conversationData,
        analysisData: analysisData,
        competenceData: competenceData,
        onAnalysisUpdate: handleAnalysisUpdate,
        onConversationUpdate: handleConversationUpdate,
        onSaveData: saveAppData,
        onLaunchSearch: handleLaunchSearch,
        onSwitchTab: setActiveTab
      })
    ]),

    // ✨ 12: Bouton de lancement avec données sourceService
    analysisData.readyToSearch && React.createElement('div', {
      key: 'launch-button',
      className: "fixed bottom-6 right-6"
    },
      React.createElement('button', {
        onClick: handleLaunchSearch,
        className: "px-6 py-3 bg-gradient-to-r from-green-500 to-blue-500 text-white rounded-full font-medium flex items-center shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
      }, [
        React.createElement(Globe, {
          key: 'icon',
          className: "h-5 w-5 mr-2"
        }),
        React.createElement('span', {
          key: 'text'
        }, 'Lancer la Recherche 360°'),
        // ✨ Badge sources sourceService si disponibles
        conversationData.relevantLinks?.length > 0 && React.createElement('span', {
          key: 'sources-count',
          className: "ml-2 text-xs bg-white bg-opacity-20 px-2 py-1 rounded-full"
        }, `+${conversationData.relevantLinks.length} sources`),
        React.createElement(ChevronRight, {
          key: 'chevron',
          className: "h-4 w-4 ml-1"
        })
      ])
    ),

    // ✨ Notification flottante pour nouvelles sources sourceService
    conversationData.sourcesGenerationTime && 
    new Date() - new Date(conversationData.sourcesGenerationTime) < 30000 && // 30 secondes
    React.createElement('div', {
      key: 'sources-notification',
      className: "fixed top-6 right-6 bg-gradient-to-r from-purple-500 to-pink-500 text-white p-4 rounded-lg shadow-lg max-w-sm animate-pulse z-50"
    }, [
      React.createElement('div', {
        key: 'content',
        className: "flex items-center"
      }, [
        React.createElement(Sparkles, {
          key: 'icon',
          className: "h-5 w-5 mr-2"
        }),
        React.createElement('div', {
          key: 'text'
        }, [
          React.createElement('p', {
            key: 'title',
            className: "font-medium text-sm"
          }, 'Sources Aladinn générées !'),
          React.createElement('p', {
            key: 'details',
            className: "text-xs opacity-90"
          }, `${conversationData.relevantLinks?.length || 0} sources vérifiées disponibles`)
        ])
      ])
    ])
  ]);
};

export default IntentAnalyzerSpace;
